"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkRelaySourceView = exports.LinkRelayAck = exports.LinkRelayPromotionCounts = exports.LinkRelayCashierCounts = exports.LinkRelayBatch = exports.LinkRelayPromotionRow = exports.LinkRelayPromotionKey = exports.LinkRelayCashier = exports.LinkRelayCashierIdentity = exports.LinkRelayPromotionTable = exports.LINK_RELAY_MAX_IDENTITY_BYTES = exports.LINK_RELAY_MAX_BODY_BYTES = exports.LINK_RELAY_MAX_ERRORS = exports.LINK_RELAY_MAX_ITEMS = exports.LINK_RELAY_FOREIGN_SOURCE = exports.LINK_RELAY_NOT_OWNER = exports.LINK_RELAY_RESYNC = exports.LINK_RELAY_SCHEMA_VERSION = void 0;
exports.cashierIdentityFits = cashierIdentityFits;
const zod_1 = require("zod");
/**
 * re:form.lite.link → Back Office: `POST /api/v1/link/relay` (RL-554).
 *
 * When a store's Link owns the store-system catalogue (`catalogueSource =
 * link`), Back Office cannot reach the store database, so the Link relays what
 * Back Office still needs from it: the store's CASHIERS and its PROMOTION
 * objects. Back Office runs them through the projections its own import uses —
 * cashiers keep Back Office's ids, promotions are projected with the same
 * outcome report — and tills receive both on the ordinary `sync.cashiers` /
 * `sync.promotions`, exactly as before.
 *
 * ------------------------------------------------------------------
 * WHAT TRAVELS
 * ------------------------------------------------------------------
 *  - a cashier is its store identity (`kassirId`, `shopId`, `login`), its
 *    fields, and the PIN HASH the Link made with the tenant pepper it was
 *    delivered (RL-451) with the `pinKeyVersion` of that pepper. The plain PIN
 *    never leaves the Link, and there is no field for it;
 *  - a promotion row is a staged `t_back_table1` (object) or `t_back_table2`
 *    (property) row, the profile's normalised payload byte for byte;
 *  - a rights row (VL-73) is a staged `t_perms` row, the same way, in
 *    `promotionRows`: Back Office derives the store's cashier-group roles
 *    from them (`isactive` 1 = granted, a missing row = not granted).
 *
 * ------------------------------------------------------------------
 * INCREMENTAL, IDEMPOTENT, RESUMABLE
 * ------------------------------------------------------------------
 * The Link remembers, per item, the digest of what Back Office has accepted,
 * and sends only what differs (and the removals). Before a batch goes out the
 * Link forgets the digest of every item in it, so a batch whose answer is
 * lost is sent again whatever the store does meanwhile — a change reverted,
 * a row added and deleted. Batches are numbered within an `epoch`:
 *  - a FULL resend starts a new epoch at sequence 1 and sends every item the
 *    store holds. Its `final` batch completes it: Back Office then drops what
 *    it was not sent in that epoch (it suspends such cashiers). No list of
 *    everything travels, so a full resend of a store at the contract's
 *    maximums stays inside the route's body limit, batch by batch. An item
 *    the Link WITHHOLDS (it breaks this contract) is not sent, but when its
 *    identity is itself valid the full resend names it in `retainedCashiers`
 *    / `retainedPromotionRows`: Back Office keeps it, unchanged, rather than
 *    dropping it. Only a withheld item whose identity cannot be sent is
 *    dropped;
 *  - every later batch must be the next sequence of the current epoch;
 *  - the batch Back Office accepted last may be sent again under the same
 *    number (its answer was lost; the Link may have folded later changes into
 *    it): it is applied again — every write converges, so only what differs
 *    changes — and answered with `duplicate: true`. It must carry the same
 *    `batchId`: the Link keeps the id of the batch it has in flight, so only
 *    a Link that knows it sent that number reuses it. A Link whose database
 *    was restored to before that batch sends the number with a new id, and
 *    is told to resync rather than have its batch taken as a resend of one
 *    it has no record of;
 *  - anything else — an unknown epoch, a gap, or Back Office asking for it —
 *    is refused with HTTP 409 and code `LINK_RELAY_RESYNC`, and the Link starts
 *    a full resend.
 * Back Office projects the promotions on the batch marked `final`, so a pass
 * split over several batches is never projected half-way.
 *
 * ------------------------------------------------------------------
 * WHICH STORE
 * ------------------------------------------------------------------
 * Back Office takes the store from the Link's own terminal record, never from
 * the body: a `sourceId` that is not a source of that store is refused (403,
 * `LINK_RELAY_FOREIGN_SOURCE`), and so is a relay for a store whose catalogue
 * the Link does not own (409, `LINK_RELAY_NOT_OWNER`).
 */
exports.LINK_RELAY_SCHEMA_VERSION = 1;
/** Refusal codes (`code` of the error body). */
exports.LINK_RELAY_RESYNC = 'LINK_RELAY_RESYNC';
exports.LINK_RELAY_NOT_OWNER = 'LINK_RELAY_NOT_OWNER';
exports.LINK_RELAY_FOREIGN_SOURCE = 'LINK_RELAY_FOREIGN_SOURCE';
/** Items (upserts + removals) per batch; the Link also keeps a batch well under the body limit. */
exports.LINK_RELAY_MAX_ITEMS = 1_000;
/** Record faults Back Office names in an answer and in the status view. */
exports.LINK_RELAY_MAX_ERRORS = 20;
/** Back Office's limit on the body of `POST /link/relay`. */
exports.LINK_RELAY_MAX_BODY_BYTES = 2 * 1024 * 1024;
/**
 * UTF-8 bytes of a cashier identity (`kassirId` + `shopId` + `login`). Back
 * Office keys the identity in a btree index, whose rows PostgreSQL caps near
 * 2.7 kB: three 512-character texts in multi-byte characters would not fit.
 * The Link withholds a cashier whose identity is longer.
 */
exports.LINK_RELAY_MAX_IDENTITY_BYTES = 1_536;
/**
 * The staged store tables a Link relays as rows (`promotionRows`): the two
 * promotions are read from (RL-467), and the cashier-group rights table
 * `t_perms` (VL-73), from which Back Office derives each store group's till
 * role. The field keeps its name for wire compatibility. A Back Office before
 * VL-73 refuses a batch with a `t_perms` row, so it is deployed first.
 */
exports.LinkRelayPromotionTable = zod_1.z.enum(['t_back_table1', 't_back_table2', 't_perms']);
const SourceText = zod_1.z.string().max(512);
const CashierIdentityShape = zod_1.z.object({
    kassirId: SourceText.min(1),
    shopId: SourceText.min(1),
    login: SourceText.min(1),
});
/** Whether a cashier identity fits `LINK_RELAY_MAX_IDENTITY_BYTES`. */
function cashierIdentityFits(identity) {
    const bytes = (text) => new TextEncoder().encode(text).length;
    return (bytes(identity.kassirId) + bytes(identity.shopId) + bytes(identity.login) <=
        exports.LINK_RELAY_MAX_IDENTITY_BYTES);
}
const IDENTITY_TOO_LONG = {
    message: `the identity is longer than the relay allows (${exports.LINK_RELAY_MAX_IDENTITY_BYTES} bytes)`,
    path: ['login'],
};
exports.LinkRelayCashierIdentity = CashierIdentityShape.refine(cashierIdentityFits, IDENTITY_TOO_LONG);
exports.LinkRelayCashier = CashierIdentityShape.extend({
    displayName: zod_1.z.string().min(1).max(120),
    groupName: SourceText.nullable(),
    cardNumber: SourceText.nullable(),
    /** `t_kassirs.id` as decimal text (a bigint). */
    rowId: zod_1.z
        .string()
        .regex(/^[0-9]{1,19}$/)
        .nullable(),
    status: zod_1.z.enum(['active', 'suspended']),
    /**
     * Argon2id of `${pin}${pepper}` (`hashCashierPin`), or null when the Link
     * holds no hash for the cashier (the store has no PIN for it, or the Link
     * had no pepper). Null leaves Back Office's credential as it is. Back Office
     * takes only a hash with the parameters `hashCashierPin` uses
     * (`isCanonicalCashierPinHash`); any other is refused for that cashier.
     */
    pinHash: zod_1.z
        .string()
        .max(512)
        .regex(/^\$argon2id\$/)
        .nullable(),
    /** The pepper version `pinHash` was made with; 0 with no hash. */
    pinKeyVersion: zod_1.z.number().int().min(0).max(1_000_000),
}).refine(cashierIdentityFits, IDENTITY_TOO_LONG);
exports.LinkRelayPromotionKey = zod_1.z.object({
    table: exports.LinkRelayPromotionTable,
    sourceKey: SourceText.min(1),
});
exports.LinkRelayPromotionRow = exports.LinkRelayPromotionKey.extend({
    /** The staged payload (`normalizeMasterkasaRow`), as the Link holds it. */
    payload: zod_1.z.record(zod_1.z.unknown()),
});
exports.LinkRelayBatch = zod_1.z
    .object({
    schemaVersion: zod_1.z.literal(exports.LINK_RELAY_SCHEMA_VERSION),
    sourceId: zod_1.z.string().min(1).max(128),
    epoch: zod_1.z.string().uuid(),
    sequence: zod_1.z.number().int().min(1),
    /**
     * Chosen by the Link when it first sends this sequence number, and kept
     * with its in-flight batch, so a resend of that number carries it again
     * (see INCREMENTAL, IDEMPOTENT, RESUMABLE).
     */
    batchId: zod_1.z.string().uuid(),
    /** Part of a full resend (every batch of it). */
    full: zod_1.z.boolean(),
    /** The last batch of this pass: Back Office projects the promotions now. */
    final: zod_1.z.boolean(),
    /**
     * VL-73: this Link reads and relays `t_perms`, even if the table has no
     * rows. An older Link omits this field; without it, an empty batch cannot
     * distinguish "the store revoked every right" from "this Link has not
     * learned the rights table yet". Back Office must not fall back to its
     * older import once this is true.
     */
    rightsTableRead: zod_1.z.boolean().optional(),
    cashiers: zod_1.z.array(exports.LinkRelayCashier).max(exports.LINK_RELAY_MAX_ITEMS).default([]),
    /** Cashiers the Link no longer holds; suspended, never deleted. */
    removedCashiers: zod_1.z.array(exports.LinkRelayCashierIdentity).max(exports.LINK_RELAY_MAX_ITEMS).default([]),
    promotionRows: zod_1.z.array(exports.LinkRelayPromotionRow).max(exports.LINK_RELAY_MAX_ITEMS).default([]),
    removedPromotionRows: zod_1.z.array(exports.LinkRelayPromotionKey).max(exports.LINK_RELAY_MAX_ITEMS).default([]),
    /**
     * Only in a full resend: store items the Link WITHHOLDS (below) whose
     * identity is itself valid. Back Office counts them as sent in this epoch
     * and leaves their data as it last received it, so the resend's final
     * batch neither suspends such a cashier nor drops such a row. They travel
     * in the resend's batches like any other entry, inside the body limit.
     */
    retainedCashiers: zod_1.z.array(exports.LinkRelayCashierIdentity).max(exports.LINK_RELAY_MAX_ITEMS).default([]),
    retainedPromotionRows: zod_1.z.array(exports.LinkRelayPromotionKey).max(exports.LINK_RELAY_MAX_ITEMS).default([]),
    /**
     * Read on the `final` batch: the store items the Link did NOT relay
     * because they break this contract (a text over its cap, a row too large
     * for a batch), described by identity, and how many there are. One bad
     * row must not stop the store's relay; Back Office shows these to the
     * operator until the Link stops listing them. Each description says what
     * Back Office does with the item: keeps what it last received of it, or —
     * its identity itself breaking the contract — drops it on a full resend.
     */
    withheld: zod_1.z.array(zod_1.z.string().max(500)).max(exports.LINK_RELAY_MAX_ERRORS).default([]),
    withheldCount: zod_1.z.number().int().min(0).default(0),
})
    .refine((batch) => batch.withheldCount >= batch.withheld.length, {
    message: '`withheldCount` counts at least the items `withheld` names',
    path: ['withheldCount'],
})
    .refine((batch) => batch.full ||
    (batch.retainedCashiers.length === 0 && batch.retainedPromotionRows.length === 0), {
    message: 'only a full resend names the withheld items Back Office keeps',
    path: ['retainedCashiers'],
});
/** What Back Office's cashier projection did with a batch (the VL-72 tally). */
exports.LinkRelayCashierCounts = zod_1.z.object({
    created: zod_1.z.number().int().min(0),
    updated: zod_1.z.number().int().min(0),
    unchanged: zod_1.z.number().int().min(0),
    suspended: zod_1.z.number().int().min(0),
    skipped: zod_1.z.number().int().min(0),
    failed: zod_1.z.number().int().min(0),
});
/** What Back Office's promotion projection did (RL-467's report). */
exports.LinkRelayPromotionCounts = zod_1.z.object({
    objects: zod_1.z.number().int().min(0),
    mapped: zod_1.z.number().int().min(0),
    unmapped: zod_1.z.number().int().min(0),
    created: zod_1.z.number().int().min(0),
    updated: zod_1.z.number().int().min(0),
    unchanged: zod_1.z.number().int().min(0),
    archived: zod_1.z.number().int().min(0),
    unreadableRecords: zod_1.z.number().int().min(0),
});
exports.LinkRelayAck = zod_1.z.object({
    receivedAt: zod_1.z.string().datetime(),
    epoch: zod_1.z.string(),
    sequence: zod_1.z.number().int().min(1),
    /** This sequence had already been accepted; the batch was applied again, converging. */
    duplicate: zod_1.z.boolean(),
    cashiers: exports.LinkRelayCashierCounts,
    /** Null unless the batch was `final` and the promotions were projected. */
    promotions: exports.LinkRelayPromotionCounts.nullable(),
    /** Record faults (a cashier Back Office could not take), identity only. */
    errors: zod_1.z.array(zod_1.z.string().max(500)).max(exports.LINK_RELAY_MAX_ERRORS),
});
// ------------------------------------------------------------------
// Operator view: one entry per source in `LinkStatusView.relay`
// ------------------------------------------------------------------
exports.LinkRelaySourceView = zod_1.z.object({
    sourceId: zod_1.z.string(),
    name: zod_1.z.string().nullable(),
    /** The Link that relays; null once reset. */
    terminalId: zod_1.z.string().nullable(),
    /** Last batch accepted. */
    lastRelayedAt: zod_1.z.string().datetime().nullable(),
    /** Last pass completed (its promotions projected). */
    lastCompletedAt: zod_1.z.string().datetime().nullable(),
    /** A full resend is under way (started, not yet complete). */
    fullInProgress: zod_1.z.boolean(),
    /** An operator asked for a full resend the Link has not started yet. */
    resendRequested: zod_1.z.boolean(),
    /** Counts of the last completed pass. */
    cashiers: exports.LinkRelayCashierCounts.nullable(),
    promotions: exports.LinkRelayPromotionCounts.nullable(),
    /**
     * Back Office's last refusal, a projection of the relayed promotions that
     * failed, the items the Link withholds (each saying whether Back Office
     * keeps it unchanged or drops it), and every relayed cashier Back Office
     * could not take as sent — a refused PIN hash, a cashier of another shop, a
     * unique field — which stays listed until the Link relays that cashier
     * cleanly or it is gone.
     */
    errors: zod_1.z.array(zod_1.z.string()),
});
//# sourceMappingURL=link-relay.js.map